import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from 'react'
import { ApolloError, useQuery } from '@apollo/client'

import {
  getSubSectionsVariables,
  getSubSectionElementsVariables,
} from '../lib/gql'

import { sectionFields } from '../__generated__/sectionFields'
import { elements, elementsVariables } from '../__generated__/elements'
import { section, sectionVariables } from '../__generated__/section'
import { ELEMENTS, SECTION, SECTIONS } from '../queries/catalogQuery'
import { CATALOG_HREF } from '../components/data'

import {
  getParentSectionElementsVariables,
  getSectionVariables,
} from '../lib/gql'
import { sections, sectionsVariables } from '../__generated__/sections'
import { LayoutContext } from './layoutProvider'
import { ICatalogItem, INavItem } from '../components/types'
import { ElementOrderByInput, SortOrder } from '../__generated__/globalTypes'
import { createCatalogElement } from '../lib/createCatalogItem'

export const ELEMENTS_ONPAGE = 9

interface ISectionContext {
  section: sectionFields
  sectionCode: string
  subSections: sectionFields[]
  elements: ICatalogItem[]
  count: number
  hasNextPage: boolean
  loading: boolean
  error?: ApolloError
  loadMore: () => Promise<void>
  isQuizOpen: boolean
  setIsQuizOpen: React.Dispatch<React.SetStateAction<boolean>>
  order: ElementOrderByInput
  setOrder: React.Dispatch<React.SetStateAction<ElementOrderByInput>>
  sortUpdate: (sort: ElementOrderByInput) => void
  refetching: boolean
}

interface ISectionProvider {
  sectionCode: string
  subSectionCode?: string
  now: string
}

export const SectionContext = createContext({} as ISectionContext)

const SectionProvider: FC<ISectionProvider> = ({
  sectionCode,
  subSectionCode,
  children,
  now,
}) => {
  const { setInnerNav } = useContext(LayoutContext)
  const [order, setOrder] = useState<ElementOrderByInput>({
    sort: SortOrder.asc,
  })
  const [isQuizOpen, setIsQuizOpen] = useState(false)
  const [refetching, setRefetching] = useState(false)

  const makeInnerNav = () => {
    const innerNav: INavItem[] = []

    innerNav.push({
      href: CATALOG_HREF,
      as: `/catalog/${sectionCode}`,
      selected: !subSectionCode,
      title: 'Все',
      code: section?.code,
    })
    dataSubSections?.sections?.nodes?.map((section) => {
      innerNav.push({
        href: CATALOG_HREF,
        as: `/catalog/${sectionCode}/${section?.code}`,
        selected: subSectionCode && subSectionCode === section?.code,
        title: section.name,
        code: `inner-nav-${section?.code}`,
      })
    })

    setInnerNav(innerNav)
  }

  // get section info
  const {
    data: dataSection,
    loading: loadingSection,
    error: errorSection,
  } = useQuery<section, sectionVariables>(SECTION, {
    variables: getSectionVariables({
      sectionCode: subSectionCode ? subSectionCode : sectionCode,
    }),
  })

  // get subsections list
  const {
    data: dataSubSections,
    loading: loadingSubSections,
    error: errorSubSections,
  } = useQuery<sections, sectionsVariables>(SECTIONS, {
    variables: getSubSectionsVariables({ sectionCode }),
  })

  // get elements list
  const elemnetsVariables = subSectionCode
    ? getSubSectionElementsVariables({
        first:
          sectionCode === 'mineralnaya_kosmetika_dlya_litsa'
            ? ELEMENTS_ONPAGE - 1
            : ELEMENTS_ONPAGE,
        sectionCode: subSectionCode,
        order,
        now,
      })
    : getParentSectionElementsVariables({
        first:
          sectionCode === 'mineralnaya_kosmetika_dlya_litsa'
            ? ELEMENTS_ONPAGE - 1
            : ELEMENTS_ONPAGE,
        sectionCode,
        order,
        now,
      })

  const { data, loading, error, fetchMore, refetch } = useQuery<
    elements,
    elementsVariables
  >(ELEMENTS, {
    variables: elemnetsVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: order.sort ? 'cache-only' : 'cache-first',
  })

  useEffect(() => {
    if (dataSubSections) {
      makeInnerNav()
    }
  }, [subSectionCode, dataSubSections])

  const cursor = data?.elements?.pageInfo?.endCursor

  const loadMore = async () => {
    if (!hasNextPage || !cursor) {
      return
    }

    const elemnetsVariables = subSectionCode
      ? getSubSectionElementsVariables({
          first: ELEMENTS_ONPAGE,
          after: cursor,
          sectionCode: subSectionCode,
          order,
          now,
        })
      : getParentSectionElementsVariables({
          first: ELEMENTS_ONPAGE,
          after: cursor,
          sectionCode,
          order,
          now,
        })

    await fetchMore({
      variables: elemnetsVariables,
    })
  }

  const sortUpdate = (sort: ElementOrderByInput) => {
    refetch({ ...elemnetsVariables, order: { ...sort } })
  }

  useEffect(() => {
    setRefetching(!cursor && !data?.elements)
  }, [cursor])

  const section = dataSection?.sections?.nodes[0]
  const subSections = dataSubSections?.sections?.nodes
  const elements = data?.elements?.edges?.map((edge) =>
    createCatalogElement(edge.node),
  )
  const count = data?.elements?.count || 0
  const hasNextPage = data?.elements?.pageInfo?.hasNextPage

  return (
    <SectionContext.Provider
      value={{
        section,
        sectionCode,
        subSections,
        elements,
        count,
        hasNextPage,
        loading: loading || loadingSection || loadingSubSections,
        error: error || errorSection || errorSubSections,
        loadMore,
        isQuizOpen,
        setIsQuizOpen,
        setOrder,
        order,
        sortUpdate,
        refetching,
      }}
    >
      {children}
    </SectionContext.Provider>
  )
}

export default SectionProvider
